import * as THREE from 'three';
import { Base3DPart } from "./base-part";

export enum TramoggiaState {
    PAUSED = 'PAUSED',
    RUNNING = 'RUNNING',
    DISABLED = 'DISABLED',
    EMPTY = 'EMPTY'
}

export class Tramoggia3DPart extends Base3DPart {
    private position: number;
    private offset: THREE.Vector3;
    private isHalfSlide: boolean;
    private isAnimationShowed: boolean = false;
    private _type: string = 'Fenix';
    private _state: TramoggiaState = TramoggiaState.DISABLED;

    private pauseSign?: THREE.Object3D;
    private runningSign?: THREE.Object3D;
    private disabledSign?: THREE.Object3D;
    private emptySign?: THREE.Object3D;

    private actualSign?: THREE.Object3D;
    private fillMesh?: THREE.Mesh;

    constructor(position: number, isHalfSlide: boolean, type: string) {
        let offset = new THREE.Vector3(0, position * 5, 0);
        if (type === 'Hyperion') {
            offset = new THREE.Vector3(0, position * 4.8, 0);
        } else if (type === 'Optica') {
            offset = new THREE.Vector3(0, position * 3.9, 0);
        }
        
        super(type, true);
        this.offset = offset;
        this._type = type;
        this.position = position;
        this.isHalfSlide = isHalfSlide;
    }

    override load(scene: THREE.Scene): void {
        const objPath = `${this.basePath}/tramoggia/t_empty.gltf`;

        this.gltfLoader.load(objPath, (gltf) => {
            if (this.isHalfSlide) {
                gltf.scene.scale.set(1, 0.5, 1);
                gltf.scene.position.add(new THREE.Vector3(0, -5, 0));
            }
            gltf.scene.position.add(this.offset);
            scene.add(gltf.scene);
        });
    }

    public toogleShowAnimation(anim: boolean): void {
        this.isAnimationShowed = anim;
        if (!anim) {
            if (this.actualSign) {
                this.actualSign.visible = false;
            }
            if (this.fillMesh) {
                this.fillMesh.visible = false;
            }
        } else {
            if (this.actualSign) {
                this.actualSign.visible = true;
            }
            if (this.fillMesh) {
                this.fillMesh.visible = true;
            }
        }
    }

    public setPercentageFilled(percentage: number, scene: THREE.Scene): void {
        const shape = new THREE.Shape();
        let depth = 4.65;

        if (this._type === 'Optica') {
            // Convert percentage (0-100) to height (0-11)
            const height = (percentage / 100) * 10.7;
            if (height > 4.1) {
                shape.moveTo(0, 0);
                shape.lineTo(-1.05, 4.1);
                shape.lineTo(-1.05, height);
                shape.lineTo(5.35, height);
                shape.lineTo(5.35, 4.1);
                shape.lineTo(4.4, 0);
                shape.lineTo(0, 0);
            } else {
                let width = (4.1 - height) / 4;
                shape.moveTo(0, 0);
                shape.lineTo(-1.05 + width, height);
                shape.lineTo(5.35 - width, height);
                shape.lineTo(4.4, 0);
                shape.lineTo(0, 0);
            }

            depth = 3.9;
            if (this.isHalfSlide) {
                depth = 1.95;
            }
        } else if (this._type === 'Hyperion') {
            // Convert percentage (0-100) to height (0-8.12)
            const height = (percentage / 100) * 8.12;
            if (height > 4.1) {
                shape.moveTo(0, 0);
                shape.lineTo(-1.28, 4.85);
                shape.lineTo(-1.28, height);
                shape.lineTo(2.9, height);
                shape.lineTo(2.9, 4.85);
                shape.lineTo(1.75, 0);
                shape.lineTo(0, 0);
            } else {
                let width = (4.85 - height) / 4;
                shape.moveTo(0, 0);
                shape.lineTo(-1.28 + width, height);
                shape.lineTo(2.9 - width, height);
                shape.lineTo(1.72, 0);
                shape.lineTo(0, 0);
            }

            depth = 4.7;
            if (this.isHalfSlide) {
                depth = 2.35;
            }
        } else {
            // Convert percentage (0-100) to height (0-11)
            const height = (percentage / 100) * 11;
            if (height > 4.1) {
                shape.moveTo(0, 0);
                shape.lineTo(-1.35, 4.1);
                shape.lineTo(-1.4, height);
                shape.lineTo(7.1, height);
                shape.lineTo(7.1, 4.1);
                shape.lineTo(5.7, 0);
                shape.lineTo(0, 0);
            } else {
                let width = (4.1 - height) / 3;
                shape.moveTo(0, 0);
                shape.lineTo(-1.35 + width, height);
                shape.lineTo(7.1 - width, height);
                shape.lineTo(5.7, 0);
                shape.lineTo(0, 0);
            }

            depth = 4.65;
            if (this.isHalfSlide) {
                depth = 2.325;
            }
        }
        
        const extrudeSettings = {
        steps: 1,
        depth: depth,
        bevelEnabled: false,
        bevelSize: 1,
        bevelOffset: 0,
        bevelSegments: 1
        };

        const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
        const material = new THREE.MeshPhongMaterial({ color: 0x00ff00, transparent: true, opacity: 0.5 });
        const mesh = new THREE.Mesh(geometry, material);
        mesh.rotation.x = Math.PI / 2;

        // Set position
        if (this._type === 'Optica') {
            mesh.position.set(-2.4, -5.4, 14.3);
        } else if (this._type === 'Hyperion') {
            mesh.position.set(-14.95, -5, 10.58);
        } else {
            mesh.position.set(-9.9, -5, 14);
        }

        if (this.isHalfSlide) {
            mesh.position.add(new THREE.Vector3(0, -2.5, 0));
        }
        mesh.position.add(this.offset);

        // Add to scene
        if (this.fillMesh) {
            scene.remove(this.fillMesh);
        }
        scene.add(mesh);
        this.fillMesh = mesh;
    }

    /**
     * 
     * @param state 
     * @param scene 
     */
    public setState(state: TramoggiaState, scene: THREE.Scene): void {
        if (this._state === state) {
            return;
        } else {
            if (this.actualSign) {
                scene.remove(this.actualSign);
            }
        }

        this._state = state;

        switch (state) {
            case TramoggiaState.PAUSED:
                const pausedSignPath = `${this.basePath}/tramoggia/pause.gltf`;
                if (!this.pauseSign) {
                    this.gltfLoader.load(pausedSignPath, (gltf) => {
                        if (this.isHalfSlide) {
                            gltf.scene.position.add(new THREE.Vector3(0, -1.3, 0));
                        }
                        gltf.scene.position.add(this.offset);
                        scene.add(gltf.scene);
                        gltf.scene.visible = false;
                        this.pauseSign = gltf.scene;
                        this.actualSign = gltf.scene
                        this.toogleShowAnimation(true);
                    });
                } else {
                    this.actualSign = this.pauseSign;
                    scene.add(this.actualSign);
                }
                this.toogleShowAnimation(true);
                break;
            case TramoggiaState.RUNNING:
                const runningSignPath = `${this.basePath}/tramoggia/run.gltf`;
                if (!this.runningSign) {
                    this.gltfLoader.load(runningSignPath, (gltf) => {
                        if (this.isHalfSlide) {
                            gltf.scene.scale.set(1, 0.5, 1);
                            gltf.scene.position.add(new THREE.Vector3(0, -5, 0));
                        }
                        gltf.scene.position.add(this.offset);
                        scene.add(gltf.scene);
                        gltf.scene.visible = false;
                        this.runningSign = gltf.scene;
                        this.actualSign = gltf.scene;
                        this.toogleShowAnimation(true);
                    });
                } else {
                    this.actualSign = this.runningSign;
                    scene.add(this.actualSign);
                }
                this.toogleShowAnimation(true);
                break;
            case TramoggiaState.DISABLED:
                const disabledSignPath = `${this.basePath}/tramoggia/disabled.gltf`;
                if (!this.disabledSign) {
                    this.gltfLoader.load(disabledSignPath, (gltf) => {
                        if (this.isHalfSlide) {
                            gltf.scene.scale.set(1, 0.5, 1);
                            gltf.scene.position.add(new THREE.Vector3(0, -5, 0));
                        }
                        gltf.scene.position.add(this.offset);
                        scene.add(gltf.scene);
                        gltf.scene.visible = false;
                        this.disabledSign = gltf.scene;
                        this.actualSign = gltf.scene
                        this.toogleShowAnimation(false);
                    });
                } else {
                    this.actualSign = this.disabledSign;
                    scene.add(this.actualSign);
                }
                this.toogleShowAnimation(false);
                break;
            case TramoggiaState.EMPTY:
                const emptySignPath = `${this.basePath}/tramoggia/empty.gltf`;
                if (!this.emptySign) {
                    this.gltfLoader.load(emptySignPath, (gltf) => {
                        if (this.isHalfSlide) {
                            gltf.scene.scale.set(1, 0.5, 1);
                            gltf.scene.position.add(new THREE.Vector3(0, -5, 0));
                        }
                        gltf.scene.position.add(this.offset);
                        scene.add(gltf.scene);
                        gltf.scene.visible = false;
                        this.emptySign = gltf.scene;
                        this.actualSign = gltf.scene
                        this.toogleShowAnimation(false);
                    });
                } else {
                    this.actualSign = this.emptySign;
                    scene.add(this.actualSign);
                }
                this.toogleShowAnimation(false);
                break;
            default:
                break;
        }
    }
}